import { Dialog } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import {
  ActiveProviderData,
  CreateProviderAppointmentResponse,
  FormStatus,
  FormSubmission,
  FormTemplateUsage,
  PaymentCollectionStrategy,
} from '@models';
import {
  ActiveBookingIntentService,
  ActiveProviderService,
  ActiveTelehealthCallService,
  AppointmentsService,
  BookingIntentCompletionService,
  BookingIntentsService,
  CHECKOUT_SERVICE,
  CONSENT_FORMS_SERVICE,
  ConsentFormService,
  FORMS_SERVICE,
  FormsStoreService,
  PatientDocumentsService,
  ProviderAppointmentsNotificationService,
  ToastService,
} from '@services';
import {
  BookAppointmentPatientDialogComponent,
  ButtonComponent,
  CheckoutDialogComponent,
  ConfirmDialogComponent,
  FileInputComponent,
  ProviderDependantHeader,
  SetupPaymentMethodDialogComponent,
  SpinnerComponent,
  TelehealthLiveControlComponent,
  ZonedDatePipe,
} from '@ui-lib';
import {
  catchError,
  EMPTY,
  finalize,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { FormBannerComponent } from '../../form-banner/form-banner.component';

@Component({
  selector: 'patient-portal-portal-header',
  imports: [
    MatIconModule,
    FormBannerComponent,
    ButtonComponent,
    SpinnerComponent,
    FileInputComponent,
    AsyncPipe,
    ZonedDatePipe,
    TelehealthLiveControlComponent,
  ],
  providers: [
    { provide: CHECKOUT_SERVICE, useExisting: BookingIntentsService },
  ],
  templateUrl: './patient-portal-header.component.html',
})
export class PatientPortalHeaderComponent
  implements OnInit, ProviderDependantHeader
{
  @ViewChild(FileInputComponent, { static: true })
  fileInputComponent!: FileInputComponent;

  private readonly dialog = inject(Dialog);
  private readonly patientDocumentsService = inject(PatientDocumentsService);
  private readonly toast = inject(ToastService);
  private readonly activeProviderService = inject(ActiveProviderService);
  private readonly bookingIntentsService = inject(BookingIntentsService);
  private readonly providerAppointmentsNotificationService = inject(
    ProviderAppointmentsNotificationService
  );
  private readonly appointmentsService = inject(AppointmentsService);
  private readonly router = inject(Router);
  private readonly activeIntentService = inject(ActiveBookingIntentService);
  private readonly bookingIntentCompletionService = inject(
    BookingIntentCompletionService
  );
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formsStore = inject(FormsStoreService);
  private readonly consentFormService = inject(ConsentFormService);
  private readonly consentFormsService = inject(CONSENT_FORMS_SERVICE);
  private readonly formsService = inject(FORMS_SERVICE);
  protected readonly telehealthCall = inject(ActiveTelehealthCallService);

  loadingProvider = signal(true);
  loadingBookingIntent = signal(true);
  loadingFile = signal(false);
  activeProvider = signal<ActiveProviderData | null>(null);

  activeProviderLoaded = output<ActiveProviderData>();

  activeBookingIntent = toSignal(
    this.activeIntentService
      .getBookingIntentData()
      .pipe(tap(() => this.loadingBookingIntent.set(false)))
  );

  nearest$ = this.appointmentsService.nearestAppointments$.pipe(
    takeUntilDestroyed(this.destroyRef),
    map(list => list[0])
  );

  forms = this.formsStore.sortedFormsSignal;

  formToShow = computed(() => this.forms()[0] ?? null);

  get resumeBookingLabel(): string {
    const intent = this.activeBookingIntent();
    if (intent?.checkoutSessionStatus === 'Complete') {
      return 'Complete booking';
    }
    return intent?.paymentCollectionStrategy ===
      PaymentCollectionStrategy.SetupWithoutCharging
      ? 'Resume setup'
      : 'Resume payment';
  }

  get canAcceptPayments(): boolean {
    return this.activeProvider()?.canAcceptPayments !== false;
  }

  get shouldShowResumeBookingAction(): boolean {
    return !!this.activeBookingIntent() && this.canAcceptPayments;
  }

  private fileData = computed(() => this.fileInputComponent.fileData());
  private fileUploadedEffect = effect(() => {
    if (!this.fileData()) {
      return;
    }

    this.loadingFile.set(true);
    this.openUploadDocumentDisclaimer();
  });

  ngOnInit(): void {
    this.formsStore.loadForms();
    this.activeProviderService
      .getActiveProviderData()
      .pipe(finalize(() => this.loadingProvider.set(false)))
      .subscribe(data => {
        this.activeProvider.set(data);

        queueMicrotask(() => {
          this.activeProviderLoaded.emit(data);
        });
      });
  }

  openFilePicker(): void {
    this.fileInputComponent.openFilePicker();
  }

  openUploadDocumentDisclaimer(): void {
    this.dialog
      .open<boolean>(ConfirmDialogComponent, {
        data: {
          title: 'Confirm uploading new document',
          message:
            'Once reviewed by your provider or after midnight of the upload day, uploaded records become part of your permanent medical record and cannot be deleted by you.',
          confirmButtonLabel: 'Confirm and upload',
          onConfirm: () => of(true),
        },
      })
      .closed.pipe(
        take(1),
        switchMap(accepted => {
          if (!accepted || !this.fileData()) {
            return EMPTY;
          }

          const formData = new FormData();
          formData.append('file', this.fileData()!);

          return this.patientDocumentsService.uploadDocument(formData).pipe(
            catchError((error: HttpErrorResponse) => {
              const fileErrors = error?.error?.errors?.File;

              if (fileErrors?.length) {
                this.toast.showWarning(fileErrors[0]);
                console.error('Error uploading document:', fileErrors[0]);
                return EMPTY;
              }

              console.error('Error uploading document:', error);
              this.toast.showWarning(
                'Failed to upload document. Please try again.'
              );
              return EMPTY;
            }),
            tap(() => this.toast.showSuccess('Document uploaded successfully.'))
          );
        }),
        finalize(() => this.loadingFile.set(false))
      )
      .subscribe();
  }

  goToAppointments(): void {
    this.router.navigateByUrl('appointments');
  }

  goToOnlineMeeting(): void {
    this.router.navigateByUrl('online-meeting');
  }

  async openNewAppointmentDialog(): Promise<void> {
    try {
      this.dialog
        .open<CreateProviderAppointmentResponse>(
          BookAppointmentPatientDialogComponent,
          { data: { activeProvider: this.activeProvider() } }
        )
        .closed.pipe(
          catchError(() => {
            this.toast.showWarning('Failed to book an appointment.');
            return EMPTY;
          })
        )
        .subscribe(result => {
          if (result) {
            this.providerAppointmentsNotificationService.emit(result);
            this.appointmentsService.reloadNearestAppointments();
            this.toast.showSuccess('Your appointment is booked.');
          }
        });
    } catch (error) {
      console.error(
        'Failed to load BookAppointmentPatientDialogComponent:',
        error
      );
    }
  }

  openCheckoutDialog() {
    if (this.activeProvider()?.canAcceptPayments === false) {
      return;
    }

    const bookingIntent = this.activeBookingIntent();
    if (!bookingIntent) {
      return;
    }

    // Payment is already done but the intent is still pending (e.g. consent forms
    // were not signed yet). Resume the consent → confirm flow instead of checkout.
    if (bookingIntent.checkoutSessionStatus === 'Complete') {
      this.bookingIntentCompletionService
        .signConsentAndConfirm(bookingIntent.id)
        .subscribe(result => {
          if (result) {
            this.activeIntentService.reload();
            this.providerAppointmentsNotificationService.emit(result);
            this.appointmentsService.reloadNearestAppointments();
            this.toast.showSuccess('Your appointment is booked.');
          }
        });
      return;
    }

    const dialogData = {
      itemId: bookingIntent.id,
      servicesCost: bookingIntent.servicesCost,
      taxPercentage: bookingIntent.taxPercentage,
    };

    if (
      bookingIntent.paymentCollectionStrategy ===
      PaymentCollectionStrategy.SetupWithoutCharging
    ) {
      this.dialog
        .open(SetupPaymentMethodDialogComponent, {
          data: dialogData,
          injector: this.injector,
        })
        .closed.subscribe();

      return;
    }

    const providerId = this.activeProvider()?.providerId;
    if (!providerId) {
      return;
    }

    this.bookingIntentsService
      .createCheckoutSession({
        itemId: bookingIntent.id,
        providerId,
      })
      .subscribe({
        next: result => {
          this.dialog
            .open(CheckoutDialogComponent, {
              data: {
                servicesCost: bookingIntent.servicesCost,
                subtotalCents: result.subtotalCents,
                taxAmountCents: result.taxAmountCents,
                totalCents: result.totalCents,
                taxPercentage: result.taxPercentage,
                checkoutUrl: result.url,
                bookingIntentId: bookingIntent.id,
              },
              injector: this.injector,
            })
            .closed.subscribe();
        },
        error: err => {
          if (
            err?.status === 409 &&
            err.error.code === 'CHECKOUT_SESSION_IS_ALREADY_COMPLETED'
          ) {
            this.toast.showSuccess('Payment has already been completed!');
          }
        },
      });
  }

  handleFillForm(form: FormSubmission | null) {
    if (!form) return;
    const { formType, id } = form;

    switch (formType) {
      case 'SmartForm':
        this.router.navigate(['forms', 'smart-form', id]);
        break;
      case 'ConsentForm':
        this.openConsentSignFlow(form);
        break;
      default:
    }
  }

  private openConsentSignFlow(form: FormSubmission) {
    if (form.formStatus === 'Sent') {
      this.consentFormsService.markAsOpened(form.id).subscribe({
        next: () => this.formsStore.loadForms(),
      });
    }
    this.consentFormsService
      .getConsentForm(form.id)
      .pipe(
        catchError(() => of(null)),
        switchMap(consent =>
          consent?.usage === FormTemplateUsage.Membership
            ? this.consentFormService
                .openMembershipSignDialog(form.id)
                .pipe(map(result => !!result?.signed))
            : this.formsService
                .getForms({
                  type: 'ConsentForm',
                  limit: 10,
                  status: FormStatus.Completed,
                  excludeStatus: true,
                })
                .pipe(
                  map(forms => forms.map(f => f.id)),
                  switchMap(ids =>
                    this.consentFormService.openSignSequence(ids)
                  ),
                  map(signed => !!signed)
                )
        )
      )
      .subscribe({
        next: shouldReload => {
          if (shouldReload) {
            this.formsStore.loadForms();
          }
        },
        error: () =>
          this.toast.showWarning(
            'Unable to load consent forms. Please try again.'
          ),
      });
  }
}
